import React, { useState } from "react";
import { FiCopy, FiCheck } from "react-icons/fi";
import styles from "./Bill.module.css";

export type BillStatus = "pending" | "paid" | "cancelled";

export type BillType =
  | "new_offer"
  | "extend_period"
  | "highlighted"
  | "recommended";

export interface BillProps {
  title: string;
  amount: number;
  grossAmount: number;
  status: BillStatus;
  billType: BillType;
  transferTitle: string;
  transferRecipient: string;
  transferAccount: string;
  createdAt?: string;
  paidAt?: string;
}

const Bill: React.FC<BillProps> = ({
  title,
  amount,
  grossAmount,
  status,
  billType,
  transferTitle,
  transferRecipient,
  transferAccount,
  createdAt,
  paidAt,
}) => {
  const [copied, setCopied] = useState("");

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);

      setCopied(key);

      setTimeout(() => {
        setCopied("");
      }, 2000);
    } catch (error) {
      console.error("Nie udało się skopiować wartości:", error);
    }
  };

  const statusLabel =
    status === "paid"
      ? "Opłacony"
      : status === "cancelled"
      ? "Anulowany"
      : "Oczekuje na płatność";

  const typeLabel =
    billType === "new_offer"
      ? "Nowa publikacja"
      : billType === "extend_period"
      ? "Przedłużenie publikacji"
      : billType === "highlighted"
      ? "Wyróżnienie wizytówki"
      : "Polecana firma";

  return (
    <div className={styles.Bill}>
      <div className={styles.Header}>
        <div>
          <h3>{title}</h3>

          <p>Typ usługi: {typeLabel}</p>

          {createdAt && (
            <p>
              Utworzono:{" "}
              {new Date(createdAt).toLocaleDateString("pl-PL")}
            </p>
          )}
        </div>

        <span className={`${styles.Status} ${styles[status]}`}>
          {statusLabel}
        </span>
      </div>

      <div className={styles.Amount}>
        <span className={styles.AmountLabel}>
          Kwota do zapłaty
        </span>

        <strong className={styles.AmountGross}>
          {grossAmount.toFixed(2)} PLN
        </strong>

        <div className={styles.AmountNet}>
          {amount.toFixed(2)} PLN netto + 23% VAT
        </div>
      </div>

      <div className={styles.Section}>
        <div>
          <span>Odbiorca</span>

          <div className={styles.CopyRow}>
            <strong>{transferRecipient}</strong>

            <button
              type="button"
              className={styles.CopyButton}
              onClick={() =>
                copy("recipient", transferRecipient)
              }
            >
              {copied === "recipient" ? (
                <FiCheck />
              ) : (
                <FiCopy />
              )}
            </button>
          </div>
        </div>

        <div>
          <span>Numer rachunku</span>

          <div className={styles.CopyRow}>
            <strong>{transferAccount}</strong>

            <button
              type="button"
              className={styles.CopyButton}
              onClick={() =>
                copy("account", transferAccount)
              }
            >
              {copied === "account" ? (
                <FiCheck />
              ) : (
                <FiCopy />
              )}
            </button>
          </div>
        </div>

        <div>
          <span>Tytuł przelewu</span>

          <div className={styles.CopyRow}>
            <strong>{transferTitle}</strong>

            <button
              type="button"
              className={styles.CopyButton}
              onClick={() =>
                copy("title", transferTitle)
              }
            >
              {copied === "title" ? (
                <FiCheck />
              ) : (
                <FiCopy />
              )}
            </button>
          </div>
        </div>
      </div>

      {paidAt && (
        <div className={styles.PaidAt}>
          Opłacono:{" "}
          {new Date(paidAt).toLocaleDateString("pl-PL")}
        </div>
      )}
    </div>
  );
};

export default Bill;